import { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Users, CalendarClock, UserCog, Stethoscope, FileCheck,
  MessageSquareWarning, FolderCheck,
  FolderOpen, DollarSign, BarChart3, Activity, Settings, Upload,
  Moon, Sun, ArrowLeft,
  Printer, ClipboardList, ShieldCheck,
  FileText, AlertTriangle, Send, ScrollText, Receipt, FileSpreadsheet,
  Shield, Heart, Target, Ban, Clock, CheckSquare,
  Wallet, MessageCircle, ClipboardCheck,
  Hospital, BookOpen, Wrench,
  PhoneCall, Kanban, TrendingUp, AlertOctagon, History,
  CalendarDays, Pill, MessageSquareHeart, Building2, Plug, DatabaseBackup,
  RefreshCw, ShieldAlert, HelpCircle, GraduationCap, ListChecks,
  BellRing, StickyNote, HeartPulse, CalendarCheck, Home,
} from "lucide-react";
import { useDarkMode } from "../../pages/healthcare/DarkModeContext";
import { DashboardMonitorIcon } from "../icons/DashboardMonitorIcon";
import { SessionHealthIndicator } from "./SessionHealthIndicator";
import { prefetchRoute } from "../../lib/routePrefetch";
import AvidaLogo from "../brand/AvidaLogo"; // eslint-disable-line no-unused-vars
import { cn } from "../../lib/utils";

// Color palette
const C = {
  navy: "#032866",
  blue: "#0866FF",
  yellow: "#FFCC00",
  navyLight: "#032866",
  bg: "#F8FAFC",
};

// Per-section title-bar accents. Replaces the single yellow pill with a
// distinct soft color per section so the menu reads as grouped zones.
const ACCENTS = {
  blue:   { light: { bg: "#DBEAFE", text: "#1E3A8A" }, dark: { bg: "rgba(37,99,235,0.20)" }, dot: "#2563EB" },
  green:  { light: { bg: "#DCFCE7", text: "#14532D" }, dark: { bg: "rgba(22,163,74,0.22)" }, dot: "#16A34A" },
  purple: { light: { bg: "#EDE9FE", text: "#4C1D95" }, dark: { bg: "rgba(124,58,237,0.24)" }, dot: "#7C3AED" },
  orange: { light: { bg: "#FFEDD5", text: "#7C2D12" }, dark: { bg: "rgba(234,88,12,0.22)" }, dot: "#EA580C" },
  red:    { light: { bg: "#FEE2E2", text: "#7F1D1D" }, dark: { bg: "rgba(220,38,38,0.24)" }, dot: "#DC2626" },
};

// ─────────────────────────────────────────────────────────────────────────────
// Sidebar navigation — Feb 2026 reorg.
// Goal: every section corresponds to ONE job a real human does, not internal
// architecture. Routes are unchanged; perm gates are preserved verbatim.
// ─────────────────────────────────────────────────────────────────────────────
const NAV = [
  // Top-level (no section header)
  { type: "item", id: "dashboard", path: "/healthcare", label: "Dashboard", icon: DashboardMonitorIcon, exact: true, perm: "dashboard.view" },
  { type: "item", id: "coordinator-tasks", path: "/healthcare/tasks", label: "My Tasks", icon: CheckSquare, perm: "dashboard.view", showTaskBadge: true },
  { type: "item", id: "relationship-log", path: "/healthcare/relationship-log", label: "Relationship Log", icon: StickyNote, perm: "relationship_log.view" },

  // ── 1. Clients ────────────────────────────────────────────────────────────
  // Just the "this person is active and getting service" stuff. Onboarding
  // workflow extracted above; Scheduling/Incidents extracted below.
  {
    type: "section", id: "clients", label: "Clients", perm: "clients.view",
    children: [
      { path: "/healthcare/patients", label: "Client Roster & Census", icon: Users, perm: "clients.view" },
      { path: "/healthcare/field-visits", label: "Field Visits", icon: ClipboardList, perm: "clients.view" },
      { path: "/healthcare/authorization-management", label: "Authorizations", icon: FileCheck, perm: "clients.view" },
      { path: "/healthcare/care-plans", label: "Care Plans", icon: HeartPulse, perm: "clients.view", showPaDecisionBadge: true },
      { path: "/healthcare/documents", label: "Client File & EHR", icon: FolderOpen, perm: "docs.view" },
      { path: "/healthcare/clients/clinical", label: "Clinical", icon: ClipboardCheck, perm: "clients.view" },
      { path: "/healthcare/clients/hospitalizations", label: "Hospitalizations", icon: Hospital, perm: "clients.view" },
      { path: "/healthcare/clients/supervisory-visits-due", label: "Supervisory Visits Due", icon: Stethoscope, perm: "clients.view" },
      { path: "/healthcare/sv-planner", label: "Weekly SV Planner", icon: CalendarCheck, perm: "clients.view" },
      { path: "/healthcare/clients/ehr-completeness", label: "EHR Completeness", icon: ShieldCheck, perm: "clients.view" },
      { path: "/healthcare/clients/agreements", label: "Agreements & Consents", icon: ClipboardList, perm: "clients.view" },
      { path: "/healthcare/clients/surveys", label: "Client Surveys", icon: MessageSquareHeart, perm: "clients.view" },
    ],
  },

  // ── 4. Supportive Care ────────────────────────────────────────────────────
  // Promoted from a floating top-level item to a proper section. The single
  // SupportiveCare.jsx page already exposes intakes/plans/risk tabs — these
  // sidebar entries deep-link to the relevant tab via ?tab=.
  // Feb 20, 2026: Renamed "Care Plans" → "Service Plans (ISPs)" to disambiguate
  // from the separate /healthcare/care-plans "Care Instruction Plans" page
  // (different concept: MCO ISP vs daily caregiver instruction sheet).
  {
    type: "section", id: "supportive-care", label: "Supportive Care", accent: "green",
    children: [
      { path: "/healthcare/supportive-care?tab=intakes", label: "Intakes", icon: Heart },
      { path: "/healthcare/supportive-care?tab=plans", label: "Service Plans (ISPs)", icon: FileText },
      { path: "/healthcare/supportive-care?tab=risk", label: "Risk Plans", icon: AlertTriangle },
      { path: "/healthcare/supportive-care/iadl-reference", label: "IADL Task Reference", icon: ClipboardList },
    ],
  },

  // ── 5. Staff ──────────────────────────────────────────────────────────────
  // Renamed from "Care Team". Supervisory Visits moved here (it's a staff
  // workflow, not an authorization). Background Checks entry removed —
  // it was a duplicate route to the same page as RNs & PCWs.
  {
    type: "section", id: "staff", label: "Staff", perm: "staff.view", accent: "purple",
    children: [
      { path: "/healthcare/caregivers", label: "RNs & PCWs", icon: Users },
      { path: "/healthcare/roster", label: "Client Assignments", icon: ClipboardList },
      { path: "/healthcare/rn-assignments", label: "RN Assignments", icon: UserCog, perm: "staff.assign" },
      { path: "/healthcare/supervisory-visits", label: "Supervisory Visits", icon: Stethoscope, perm: "clinical.supervisory_visits" },
    ],
  },

  // ── 6. Scheduling & EVV ───────────────────────────────────────────────────
  // EVV is an operational concern (caregiver checking in right now), not a
  // back-office finance concern. Pulled out of Billing's "Pre-bill" subgroup.
  {
    type: "section", id: "scheduling-evv", label: "Scheduling & EVV", perm: "schedule.view", accent: "orange",
    children: [
      { path: "/healthcare/schedule", label: "Schedule", icon: CalendarClock, perm: "schedule.view" },
      { path: "/healthcare/my-calendar", label: "My Calendar", icon: CalendarDays, perm: "dashboard.view" },
      { path: "/healthcare/scheduling/coverage", label: "Shift Coverage", icon: AlertOctagon, perm: "schedule.view" },
      { path: "/healthcare/evv-workflow", label: "EVV Workflow", icon: ClipboardList, perm: "evv.view" },
      { path: "/healthcare/live-in", label: "Live-In Caregivers", icon: Home, perm: "evv.view" },
      { path: "/healthcare/evv/go-live", label: "In-App EVV Go-Live", icon: ClipboardList, perm: "evv.approve" },
      { path: "/healthcare/evv-alerts", label: "EVV Alerts", icon: BellRing, perm: "evv.view" },
      { path: "/healthcare/incomplete-task-report", label: "Incomplete Tasks (DHS)", icon: ClipboardList, perm: "evv.view" },
      { path: "/healthcare/evv/geofence-exceptions", label: "Geofence Exceptions", icon: AlertOctagon, perm: "evv.view" },
      { path: "/healthcare/sandata-aggregator", label: "Sandata Aggregator", icon: RefreshCw, perm: "evv.view" },
      { path: "/healthcare/sandata-export", label: "Sandata Export", icon: FileSpreadsheet, perm: "evv.view" },
      { path: "/healthcare/sandata-golive", label: "Sandata Go-Live Checklist", icon: FileCheck, perm: "billing.view" },
      { path: "/healthcare/timesheets", label: "Timesheets", icon: Hospital },
      { path: "/healthcare/pcw-timesheets", label: "PCW Paper Timesheets", icon: FileText, perm: "clients.view" },
      { path: "/healthcare/billing-readiness", label: "Billing Readiness", icon: ShieldCheck },
    ],
  },

  // ── 7. Billing ────────────────────────────────────────────────────────────
  // Pure revenue cycle now that pre-bill stuff lives under Scheduling & EVV.
  {
    type: "section", id: "billing", label: "Billing", perm: "billing.view", accent: "green",
    children: [
      { path: "/healthcare/billing", label: "Billing Dashboard", icon: DollarSign },
      { path: "/healthcare/claims", label: "Claims", icon: Receipt },
      { path: "/healthcare/claim-hold-queue", label: "Claim-Hold Queue", icon: AlertOctagon },
      { path: "/healthcare/availity", label: "Clearinghouse (Availity)", icon: Send },
      { path: "/healthcare/invoices", label: "Private-Pay Invoices", icon: FileSpreadsheet },
      { path: "/healthcare/reports/payroll-summary", label: "Payroll", icon: DollarSign },
      { path: "/healthcare/reports", label: "Reports Hub", icon: BarChart3 },
      { path: "/healthcare/usage-limits", label: "Usage & Limits", icon: Activity },
    ],
  },

  // ── 7b. Reconciliation ────────────────────────────────────────────────────
  // Payment posting + AR aging + payroll reconciliation — closes the loop
  // between "we billed X" and "we actually got paid X".
  {
    type: "section", id: "reconciliation", label: "Reconciliation", perm: "billing.view", accent: "green",
    children: [
      { path: "/healthcare/payments", label: "Payment Posting", icon: DollarSign },
      { path: "/healthcare/ar-aging", label: "AR Aging", icon: TrendingUp },
      { path: "/healthcare/chase-queue", label: "Chase Queue", icon: PhoneCall },
      { path: "/healthcare/disposition-queue", label: "Disposition Queue", icon: ListChecks },
      { path: "/healthcare/adjustment-rules", label: "Adjustment Rules", icon: ShieldCheck },
      { path: "/healthcare/reconciliation-reports", label: "Recon Reports", icon: ScrollText },
      { path: "/healthcare/payroll-recon", label: "Payroll Reconciliation", icon: Wallet },
      { path: "/healthcare/payroll-comparison", label: "Payroll Pilot Comparison", icon: Wallet },
      { path: "/healthcare/pay-rates", label: "Pay Rates & OT", icon: DollarSign },
      { path: "/healthcare/pay-periods", label: "Pay Periods", icon: ClipboardCheck },
      { path: "/healthcare/month-end", label: "Month-End Close", icon: ScrollText },
    ],
  },

  // ── 9. Communication ──────────────────────────────────────────────────────
  // Knowledge Base added here so it's findable from the sidebar (was only
  // reachable via the top-of-screen breadcrumb / direct URL).
  {
    type: "section", id: "communication", label: "Communication", accent: "blue",
    children: [
      { path: "/healthcare/fax", label: "Fax Center", icon: Printer, perm: "fax.view" },
      { path: "/tools/word-processor", label: "Word Processor", icon: FileText },
      { path: "/knowledge-base", label: "Knowledge Base", icon: BookOpen },
      { path: "/healthcare/lifecycles", label: "Lifecycles", icon: Activity },
    ],
  },

  // ── Compliance & Reports (just above Admin per Dawn 2026-06) ─────────────
  // Incident Reporting lives here (it's a compliance event that may trigger
  // state notification). accent:"red" renders a red tab instead of yellow.
  {
    type: "section", id: "compliance-reports", label: "Compliance & Reports", perm: "compliance.view", accent: "red",
    children: [
      { path: "/healthcare/compliance-companion", label: "Compliance Companion", icon: ShieldCheck },
      { path: "/healthcare/compliance-qa", label: "QA & Compliance", icon: ShieldCheck },
      { path: "/admin/phi-watchdog", label: "PHI Watchdog", icon: ShieldAlert, perm: "compliance.view" },
      { path: "/healthcare/admin/ai-governance", label: "AI Governance", icon: ShieldCheck, perm: "compliance.view" },
      { path: "/healthcare/audit-log", label: "Audit Trail", icon: Shield },
      { path: "/healthcare/incidents", label: "Incident Reporting", icon: AlertTriangle, perm: "incidents.view" },
      { path: "/healthcare/clinical-compliance", label: "Clinical Compliance", icon: Stethoscope, perm: "compliance.view" },
      { path: "/healthcare/grievances", label: "Grievances & CAPs", icon: MessageSquareWarning, perm: "compliance.view" },
      { path: "/healthcare/surveyor-packet", label: "Surveyor Packet", icon: FolderCheck, perm: "compliance.view" },
      { path: "/healthcare/physician-portal", label: "Physician Portal", icon: Stethoscope, perm: "compliance.view" },
      { path: "/healthcare/analytics", label: "Reports", icon: BarChart3, perm: "reports.view" },
    ],
  },

  // ── Admin (bottom, super-admin only) ──────────────────────────────────────
  // Imports moved into an "Imports" subgroup; Setup remains the catch-all
  // configuration page.
  {
    type: "section", id: "admin", label: "Admin", accent: "purple",
    children: [
      { type: "subgroup", label: "Tools" },
      { path: "/healthcare/admin/duplicate-scan", label: "Duplicate Scan", icon: Wrench, perm: "admin.settings" },
      { path: "/healthcare/admin/auth-recon", label: "Auth Reconciliation", icon: ListChecks, perm: "admin.settings" },
      { path: "/admin/backups", label: "Backups", icon: DatabaseBackup, perm: "admin.settings" },
      { type: "subgroup", label: "Setup" },
      { path: "/healthcare/admin/initial-setup", label: "Initial Setup", icon: CheckSquare, perm: "admin.settings" },
      { path: "/admin/contact-registry", label: "Contact Registry", icon: MessageCircle, perm: "admin.settings" },
      { path: "/healthcare/admin/mcos", label: "MCO Directory", icon: Building2, perm: "admin.settings" },
      { path: "/healthcare/providers", label: "Provider Directory", icon: Stethoscope, perm: "clients.view" },
      { path: "/healthcare/admin/integrations", label: "Integrations", icon: Plug, perm: "admin.settings" },
      { path: "/healthcare/admin/icd10", label: "ICD-10 Codes", icon: FileSpreadsheet, perm: "admin.settings" },
      { path: "/healthcare/admin", label: "Setup", icon: Settings, perm: "admin.settings" },
    ],
  },
];

const PCW_NAV = [
  { type: "item", id: "portal", path: "/healthcare/pcw-portal", label: "My Portal", icon: ClipboardList, exact: true },
  { type: "item", id: "schedule", path: "/healthcare/schedule", label: "Schedule", icon: CalendarClock },
];

export const HealthcareSidebar = ({ mobileOpen = false, onMobileClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { dark, toggle } = useDarkMode();
  const [userPerms, setUserPerms] = useState(null);

  const userStr = localStorage.getItem("user");
  const userRole = userStr ? (() => { try { return JSON.parse(userStr).role || "admin"; } catch { return "admin"; } })() : "admin";
  const isPcw = userRole === "pcw";

  // Auto-close the mobile drawer when the route changes — phones don't
  // have a desktop's persistent rail, so a tap-to-navigate should
  // collapse the menu the same way Slack / Linear / Notion do.
  useEffect(() => {
    if (mobileOpen && onMobileClose) onMobileClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Fetch permissions once (cookie auth — DEF-23)
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/permissions`, {
      credentials: "include",
    })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setUserPerms(new Set(d.permissions)); })
      .catch(() => {});
  }, []);

  const hasPerm = (perm) => {
    if (!perm) return true; // no perm required = always visible
    if (!userPerms) return userRole === "super_admin" || userRole === "admin" || userRole === "operator" || userRole === "global_operator"; // fallback while loading
    return userPerms.has(perm);
  };

  // Filter NAV by permissions. A section is hidden only if:
  //  - its own perm (if any) is denied, OR
  //  - it has children and every child is filtered out (and it has no note)
  const navItems = isPcw
    ? PCW_NAV
    : NAV.filter(item => hasPerm(item.perm)).map(item => {
        if (item.type === "section" && item.children) {
          // Pass 1 — drop children that fail the perm check.
          const permitted = item.children.filter(c => c.type === "subgroup" || hasPerm(c.perm));
          // Pass 2 — drop subgroup labels that no longer have any clickable
          // items beneath them (avoids orphan subheaders when all items in a
          // group are gated out for the current user's role).
          const visibleChildren = permitted.filter((c, i) => {
            if (c.type !== "subgroup") return true;
            for (let j = i + 1; j < permitted.length; j++) {
              if (permitted[j].type === "subgroup") break;
              return true;
            }
            return false;
          });
          return { ...item, children: visibleChildren };
        }
        return item;
      }).filter(item => {
        if (item.type === "section") {
          // Keep clinical-style note sections even without children
          if (item.note) return true;
          return (item.children || []).some(c => c.type !== "subgroup");
        }
        return true;
      });

  const isActive = (item) => {
    const path = location.pathname;
    const cleanPath = (item.path || "").split("?")[0];
    if (item.exact) return path === cleanPath;
    return path === cleanPath || path.startsWith(cleanPath + "/");
  };

  // ── Live coordinator task-summary count — powers the nav badge ──
  // Combines an initial REST fetch + a WebSocket subscription for instant
  // updates whenever any fax mutation happens anywhere in the app. Falls
  // back to 60s polling if the socket disconnects.
  const [taskCounts, setTaskCounts] = useState(null);
  const [awaitingDecisions, setAwaitingDecisions] = useState(0);

  // ── Collapsible section headers (persisted to localStorage) ──
  // Each section title doubles as a toggle. Closed state remembers across
  // reloads so coordinators don't have to re-collapse Admin every time.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const raw = localStorage.getItem("hc.sidebar.collapsedSections");
      return raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      return new Set();
    }
  });
  const toggleSection = (id) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      try { localStorage.setItem("hc.sidebar.collapsedSections", JSON.stringify([...next])); } catch {}
      return next;
    });
  };
  useEffect(() => {
    let mounted = true;
    let ws = null;
    let pollTimer = null;
    let reconnectTimer = null;

    const fetchCounts = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/coordinator/task-summary/counts`,
          { withCredentials: true },
        );
        if (mounted) setTaskCounts(res.data);
      } catch { /* silent — nav badge is best-effort */ }
    };

    const startPolling = () => {
      if (pollTimer) return;
      pollTimer = setInterval(fetchCounts, 60000);
    };
    const stopPolling = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    const connectWs = () => {
      // WS auth rides on the httpOnly session cookie (DEF-23).
      if (!localStorage.getItem("user") || !mounted) return;
      try {
        const backend = process.env.REACT_APP_BACKEND_URL || "";
        const wsUrl = backend.replace(/^http/, "ws") +
          `/api/coordinator/ws/tasks`;
        ws = new WebSocket(wsUrl);
        ws.onopen = () => {
          // WS is live — stop the polling fallback
          stopPolling();
        };
        ws.onmessage = (ev) => {
          try {
            const data = JSON.parse(ev.data);
            if (data && data.type === "task_counts" && mounted) {
              setTaskCounts({
                needs_action: data.needs_action,
                waiting_on_external: data.waiting_on_external,
                stale_cases: data.stale_cases,
                all: data.all,
              });
            }
          } catch { /* ignore malformed */ }
        };
        ws.onclose = () => {
          ws = null;
          if (!mounted) return;
          startPolling();
          reconnectTimer = setTimeout(connectWs, 10000);
        };
        ws.onerror = () => { try { ws && ws.close(); } catch { /* ignore */ } };
      } catch {
        startPolling();
      }
    };

    fetchCounts();
    connectWs();
    return () => {
      mounted = false;
      stopPolling();
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws && ws.close(); } catch { /* ignore */ }
    };
  }, []);

  // ── Awaiting PA-decision count — powers the Care Plans nav badge ──
  useEffect(() => {
    let mounted = true;
    const fetchAwaiting = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth-cases/awaiting-decision/count`,
          { withCredentials: true },
        );
        if (mounted) setAwaitingDecisions(res.data?.count || 0);
      } catch { /* silent — nav badge is best-effort */ }
    };
    fetchAwaiting();
    const id = setInterval(fetchAwaiting, 60000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  // ── Render helpers ────────────────────────────────────────────────────────
  const renderItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item);
    return (
      <NavLink
        key={item.path || item.id}
        to={item.path}
        onMouseEnter={() => prefetchRoute(item.path)}
        onFocus={() => prefetchRoute(item.path)}
        className={cn(
          "flex items-center gap-2.5 px-3 py-2 text-[13px] font-black rounded-xl transition-all duration-150",
          active
            ? dark ? "text-white bg-[#1a2d42]" : "text-[#032866] bg-[#FFDA00]/15"
            : dark ? "text-slate-300 hover:text-white hover:bg-[#1a2d42]" : "text-[#032866] hover:text-[#032866] hover:bg-[#F1F5F9]"
        )}
        style={active && !dark ? { borderLeft: `3px solid ${C.yellow}` } : { borderLeft: "3px solid transparent" }}
        data-testid={`nav-${(item.id || item.label).toString().toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
      >
        <Icon className="w-[18px] h-[18px] flex-shrink-0" style={active && !dark ? { color: C.navy } : {}} />
        <span className="truncate flex-1">{item.label}</span>
        {item.showTaskBadge && taskCounts && taskCounts.all > 0 && (
          <span
            className="ml-auto inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full text-[11px] font-black text-[#032866]"
            style={{ background: C.yellow }}
            title={`${taskCounts.needs_action} need action · ${taskCounts.waiting_on_external} waiting · ${taskCounts.stale_cases} stale`}
            data-testid="nav-task-count-badge"
          >
            {taskCounts.all > 99 ? "99+" : taskCounts.all}
          </span>
        )}
      </NavLink>
    );
  };

  const renderChild = (child) => {
    // Inline subgroup divider — non-clickable mini-header that visually
    // separates clusters of related items inside a single section.
    if (child.type === "subgroup") {
      return (
        <div
          key={`subgroup-${child.label}`}
          className={cn(
            "px-3 pt-2 pb-0.5 text-[9.5px] font-black uppercase tracking-[0.18em]",
            dark ? "text-slate-500" : "text-[#032866]/70"
          )}
          data-testid={`nav-subgroup-${child.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
        >
          {child.label}
        </div>
      );
    }
    const ChildIcon = child.icon;
    const childActive = isActive(child);
    return (
      <NavLink
        key={child.path}
        to={child.path}
        onMouseEnter={() => prefetchRoute(child.path)}
        onFocus={() => prefetchRoute(child.path)}
        className={cn(
          "flex items-center gap-2 px-2.5 py-1.5 text-[12.5px] font-black rounded-lg transition-all duration-150",
          childActive
            ? dark ? "text-white bg-[#1a2d42]" : "bg-[#FFDA00]/15"
            : dark ? "text-slate-300 hover:text-white" : "text-[#032866] hover:text-[#032866] hover:bg-[#F8FAFC]"
        )}
        style={childActive && !dark ? { color: C.navy, borderLeft: `2px solid ${C.yellow}` } : { borderLeft: "2px solid transparent" }}
        data-testid={`nav-${child.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
      >
        {ChildIcon ? <ChildIcon className="w-[14px] h-[14px] flex-shrink-0 opacity-70" /> : null}
        <span className="truncate flex-1">{child.label}</span>
        {child.showPaDecisionBadge && awaitingDecisions > 0 && (
          <span
            className="ml-auto inline-flex items-center justify-center min-w-[20px] h-[18px] px-1.5 rounded-full text-[10px] font-black text-white animate-pulse"
            style={{ background: "#ef4444" }}
            title={`${awaitingDecisions} client${awaitingDecisions === 1 ? "" : "s"} awaiting an MCO decision`}
            data-testid="nav-pa-decision-badge"
          >
            {awaitingDecisions > 99 ? "99+" : awaitingDecisions}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <>
      {/* Mobile backdrop — tap to close, only rendered when the drawer
          is open below lg. Doesn't render on desktop because the rail
          is permanent. */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/40 z-30"
          onClick={onMobileClose}
          aria-hidden="true"
          data-testid="healthcare-sidebar-backdrop"
        />
      )}
      <aside
        className={cn(
          // Desktop: fixed rail at left:64px (after the outer Sidebar).
          // Mobile: slides in from left at left:0, full-height, narrower
          // viewport-aware width so it doesn't dwarf the 393 px iPhone.
          "fixed top-0 h-full w-[260px] flex-col z-40 border-r transition-transform duration-300",
          "lg:left-16 lg:w-56 lg:flex lg:translate-x-0",
          "left-0 flex",
          mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          dark ? "bg-[#0B1929] border-[#1a2d42]" : "bg-white border-[#E8EDF2]"
        )}
        data-testid="healthcare-sidebar"
      >
      {/* Header */}
      <div
        className={cn("px-3 py-4 border-b", dark ? "border-[#1a2d42]" : "border-[#E8EDF2]")}
        style={!dark ? { background: "linear-gradient(135deg, #032866 0%, #003A8A 100%)", borderBottom: `3px solid ${C.yellow}` } : {}}
      >
        <div className="flex items-center gap-2">
          {/* Full-color Avida house-and-leaf mark on a white tile so it reads on the navy header */}
          <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-xl bg-white shadow-sm p-1" data-testid="sidebar-logo">
            <img src="/avida-logo-mark.png" alt="Avida" className="w-full h-full object-contain" draggable={false} />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className={cn("text-[14px] font-black truncate leading-tight", dark ? "text-white" : "text-white")}>
              Avida Health
            </h2>
            <p className={cn("text-[9.5px] leading-tight font-medium truncate", dark ? "text-slate-500" : "text-white/70")}>
              Personal Care & Supportive Services
            </p>
          </div>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <SessionHealthIndicator dark={dark} />
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-2 overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
        <div className="space-y-0.5 px-2">
          {navItems.map((item) => {
            if (item.type === "section") {
              const isCollapsed = collapsedSections.has(item.id);
              const accent = ACCENTS[item.accent] || ACCENTS.blue;
              return (
                <div key={item.id} className="pt-3 first:pt-1" data-testid={`nav-section-${item.id}`}>
                  {/* Section header — clickable pill that collapses the section.
                      Each section has its own accent color; bold uppercase
                      title, chevron indicates open/closed state. */}
                  <button
                    type="button"
                    onClick={() => toggleSection(item.id)}
                    aria-expanded={!isCollapsed}
                    aria-controls={`nav-section-body-${item.id}`}
                    className={cn(
                      "w-full mx-1 px-3 py-2.5 rounded-md flex items-center justify-between gap-1.5 transition-all text-left hover:brightness-95",
                      "text-[14px] font-black uppercase tracking-normal",
                      dark ? "text-white" : "",
                    )}
                    style={!dark
                      ? { width: "calc(100% - 8px)", backgroundColor: accent.light.bg, color: accent.light.text, fontWeight: 900, WebkitTextStroke: `0.4px ${accent.light.text}`, letterSpacing: "0.02em" }
                      : { backgroundColor: accent.dark.bg, color: "#FFFFFF", fontWeight: 900, WebkitTextStroke: "0.4px #FFFFFF", letterSpacing: "0.02em" }
                    }
                    data-testid={`nav-section-header-${item.id}`}
                  >
                    <span className="flex items-center gap-1.5 min-w-0">
                      <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: accent.dot }} aria-hidden="true" />
                      <span className="truncate">{item.label}</span>
                    </span>
                    <span className={cn(
                      "transition-transform duration-150 flex-shrink-0",
                      isCollapsed ? "-rotate-90" : "rotate-0"
                    )} aria-hidden="true">
                      <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor">
                        <path d="M2 4l4 4 4-4z" />
                      </svg>
                    </span>
                  </button>

                  {/* Section body — hidden when collapsed */}
                  {!isCollapsed && (
                    <div id={`nav-section-body-${item.id}`}>
                      {item.note ? (
                        <div
                          className={cn(
                            "mx-3 mt-1 px-3 py-2 text-[11.5px] italic leading-snug rounded-lg border-l-2",
                            dark ? "text-slate-400 border-[#FFDA00]/40 bg-[#1a2d42]/40" : "text-[#4A6280] bg-[#FFDA00]/10"
                          )}
                          style={!dark ? { borderLeft: `3px solid ${C.yellow}` } : {}}
                          data-testid={`nav-section-note-${item.id}`}
                        >
                          {item.note}
                        </div>
                      ) : (
                        <div className="space-y-0.5 mt-1">
                          {(item.children || []).map(renderChild)}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            return renderItem(item);
          })}
        </div>
      </nav>

      {/* Bottom */}
      <div className={cn("px-2 py-2.5 border-t space-y-1", dark ? "border-[#1a2d42]" : "border-[#E8EDF2]")}>
        <div
          className={cn(
            "flex items-center justify-between px-3 py-1.5 text-[11px]",
            dark ? "text-slate-500" : "text-[#4A6280]"
          )}
          data-testid="hc-sidebar-cmdk-hint"
        >
          <span className="font-medium">Jump to page</span>
          <span className="font-mono">
            <kbd className={cn("px-1.5 py-0.5 rounded border text-[10px] font-bold", dark ? "border-[#1a2d42] bg-[#0B1929]" : "border-[#032866]/20 bg-[#FFDA00]/20 text-[#032866]")}>⌘</kbd>
            <span className="mx-0.5">+</span>
            <kbd className={cn("px-1.5 py-0.5 rounded border text-[10px] font-bold", dark ? "border-[#1a2d42] bg-[#0B1929]" : "border-[#032866]/20 bg-[#FFDA00]/20 text-[#032866]")}>K</kbd>
          </span>
        </div>
        <button
          onClick={() => {
            if (window.__avidaQuickActions?.openHelp) {
              window.__avidaQuickActions.openHelp();
            } else {
              navigate("/healthcare/guide");
            }
          }}
          className={cn(
            "w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-semibold transition-all",
            dark ? "text-blue-300 hover:bg-[#1a2d42]" : "text-[#032866] hover:bg-[#F1F5F9]"
          )}
          data-testid="hc-sidebar-help"
          title="Open the Help palette (press ? anywhere)"
        >
          <HelpCircle className="w-4 h-4" />
          Help & Shortcuts
        </button>
        <button
          onClick={() => navigate("/training/downloads")}
          className={cn(
            "w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-semibold transition-all",
            dark ? "text-blue-300 hover:bg-[#1a2d42]" : "text-[#032866] hover:bg-[#F1F5F9]"
          )}
          data-testid="hc-sidebar-training-downloads"
          title="Download the Master Training Deck, manual and other assets"
        >
          <GraduationCap className="w-4 h-4" />
          Training Downloads
        </button>
        <button
          onClick={toggle}
          className={cn("w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-semibold transition-all", dark ? "text-yellow-400 hover:bg-[#1a2d42]" : "text-[#4A6280] hover:bg-[#F1F5F9] hover:text-[#032866]")}
          data-testid="hc-sidebar-darkmode"
        >
          {dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          {dark ? "Light Mode" : "Dark Mode"}
        </button>
        <button
          onClick={() => navigate("/dashboard")}
          className={cn("w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-[12px] font-semibold transition-all", dark ? "text-slate-500 hover:text-white hover:bg-[#1a2d42]" : "text-[#7B8FA3] hover:text-[#032866] hover:bg-[#F1F5F9]")}
          data-testid="hc-sidebar-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>
      </div>
    </aside>
    </>
  );
};
